import React, { useCallback, useEffect, useState } from 'react';
import { Check, Loader2, RotateCcw, Wrench, X } from 'lucide-react';
import { apiService } from '../services/api';

// Ce qui manque à Nexus (admins) : il le signale lui-même quand une demande bute sur un outil absent ; on relit, puis
// « Ajouté » ou « Écarté ». Un manque signalé de nouveau après « Ajouté » revient dans les ouverts.
const STATUS = {
  open: { label: 'À regarder', tone: 'bg-amber-50 text-amber-800' },
  added: { label: 'Ajouté', tone: 'bg-emerald-50 text-emerald-700' },
  declined: { label: 'Écarté', tone: 'bg-gray-100 text-gray-500' },
};

const when = (seconds) => new Date(seconds * 1000).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

const NexusMissingTools = () => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => apiService.get('/nexus/rights/missing-tools')
    .then((data) => setItems(data.items || []))
    .catch((err) => setError(err.message)), []);
  useEffect(() => { load(); }, [load]);

  const decide = async (item, status) => {
    setBusy(item.id);
    try {
      await apiService.patch(`/nexus/rights/missing-tools/${item.id}`, { status });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(null);
    }
  };

  const open = (items || []).filter((item) => item.status === 'open').length;
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-gray-100 px-4 py-2">
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Wrench className="h-4 w-4 text-gray-500" /> Ce qui manque à Nexus
          {open > 0 && <span className="rounded-full bg-amber-100 px-2 text-xs font-medium text-amber-800">{open}</span>}
        </h2>
        <p className="text-xs text-gray-500">Signalé par Nexus quand une demande bute sur un outil absent.</p>
      </div>
      {error && <p className="px-4 py-2 text-xs text-red-600">{error}</p>}
      {items === null ? (
        <div className="px-4 py-3"><Loader2 className="h-4 w-4 animate-spin text-gray-400" /></div>
      ) : items.length === 0 ? (
        <p className="px-4 py-3 text-sm text-gray-500">Rien de signalé pour l'instant.</p>
      ) : (
        <ul className="divide-y divide-gray-50">
          {items.map((item) => (
            <li key={item.id} className="flex flex-wrap items-start gap-3 px-4 py-2.5 text-sm">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-gray-900">{item.missing}</p>
                {item.need && <p className="text-xs text-gray-600">Demande : {item.need}</p>}
                {item.workaround && <p className="text-xs text-gray-500">À la place : {item.workaround}</p>}
                <p className="mt-0.5 text-[11px] text-gray-400">
                  {item.count > 1 ? `${item.count} fois · ` : ''}{item.employee_name || '—'} · dernier le {when(item.last_at)}
                </p>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS[item.status]?.tone}`}>{STATUS[item.status]?.label}</span>
              <div className="flex gap-1">
                {item.status === 'open' ? (
                  <>
                    <button type="button" disabled={busy === item.id} onClick={() => decide(item, 'added')}
                      className="flex items-center gap-1 rounded-md border border-emerald-200 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50">
                      <Check className="h-3.5 w-3.5" /> Ajouté
                    </button>
                    <button type="button" disabled={busy === item.id} onClick={() => decide(item, 'declined')}
                      className="flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">
                      <X className="h-3.5 w-3.5" /> Écarté
                    </button>
                  </>
                ) : (
                  <button type="button" disabled={busy === item.id} onClick={() => decide(item, 'open')} title="Rouvrir"
                    className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700">
                    <RotateCcw className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default NexusMissingTools;
